import { motion } from "framer-motion";
import { useState } from "react";
import { Calculator, BookOpen, Briefcase, ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { handleHashLink } from "../../lib/scrollUtils";

const services = [
  {
    id: "tax",
    number: "01",
    title: "Tax Strategy & Preparation",
    description:
      "Tax strategy isn't just a once-a-year errand—it's a year-round advantage. We optimize your tax position while keeping you fully compliant with complex Federal and California state tax laws.",
    icon: Calculator,
    image: "https://images.pexels.com/photos/6863254/pexels-photo-6863254.jpeg",
    features: [
      "Individual & Business Tax Returns",
      "Proactive Tax Strategy & Planning",
      "State Tax Compliance",
      "Tax Notices",
    ],
  },
  {
    id: "bookkeeping",
    number: "02",
    title: "Virtual Bookkeeping & Accounting",
    description:
      "Keep your books accurate, clean, and real-time ready. We streamline your back office so you always know where your business stands.",
    icon: BookOpen,
    image: "https://images.pexels.com/photos/6345291/pexels-photo-6345291.jpeg",
    features: [
      "Monthly reconciliation & ledger management",
      "Accurate P&L and balance sheet statements",
      "Payroll management and 1099 compliance",
      "Cloud integration (QuickBooks, Xero, Gusto)",
    ],
  },
  {
    id: "consulting",
    number: "03",
    title: "Industries We Serve",
    description:
      "Different industries face unique financial compliance rules, tax codes, and operational hurdles. We provide specialized accounting, tax, and advisory solutions tailored to high-growth, high-regulation sectors across the Golden State.",
    icon: Briefcase,
    image: "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg",
    features: [
      "Medical, Dental & Healthcare Practices",
      "Real Estate Investors & Property Management",
      "Professional Services Firms",
      "Technology, SaaS & Startups",
    ],
  },
];

const ServiceCard = ({ service, index }) => {
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [hover, setHover] = useState(false);
  const location = useLocation();

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.9, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      onMouseMove={onMove}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group relative bg-white border border-[#EBE6DD] rounded-sm overflow-hidden flex flex-col transition-all duration-500 hover:border-[#C85A43]/40"
      data-testid={`service-card-${service.id}`}
    >
      {/* Spotlight following cursor */}
      <div
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-[1]"
        style={{
          background: `radial-gradient(400px circle at ${pos.x}% ${pos.y}%, rgba(200,90,67,0.08), transparent 60%)`,
        }}
      />

      {/* Image with clipped frame */}
      <div className="relative overflow-hidden h-56">
        <motion.img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover"
          animate={{ scale: hover ? 1.08 : 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="absolute inset-0 bg-[#F9F8F6]/60"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1C1917]/25"></div>

        {/* Big serif number */}
        <div className="absolute top-6 left-6 z-10">
          <span className="font-heading text-6xl font-light italic text-[#1C1917] leading-none">
            {service.number}
          </span>
        </div>

        {/* Icon badge */}
        <div className="absolute bottom-6 right-6 z-10">
          <div className="w-12 h-12 bg-[#1C1917] group-hover:bg-[#C85A43] rounded-full flex items-center justify-center transition-colors duration-500">
            <service.icon className="w-5 h-5 text-white" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-8 flex flex-col flex-1 relative z-10">
        <h3 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#1C1917] mb-4 leading-tight">
          {service.title}
        </h3>
        <p className="text-[#3A3634] text-sm leading-relaxed mb-6">
          {service.description}
        </p>

        <ul className="space-y-3 mb-8 flex-1">
          {service.features.map((feature, i) => (
            <motion.li
              key={feature}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.08 }}
              className="flex items-start gap-3 text-sm text-[#1C1917]"
            >
              <span className="text-[#C85A43] font-medium mt-0.5 flex-shrink-0">
                —
              </span>
              <span className="font-medium">{feature}</span>
            </motion.li>
          ))}
        </ul>

        <Link
          to="/#contact"
          onClick={(e) => handleHashLink(e, "contact", location.pathname)}
          className="inline-flex items-center gap-2 text-[#1C1917] group-hover:text-[#C85A43] text-sm font-semibold tracking-wide transition-colors mt-auto"
          data-testid={`service-link-${service.id}`}
        >
          <span>Get in Touch</span>
          <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </div>
    </motion.article>
  );
};

const ServicesSection = () => {
  return (
    <section
      id="services"
      className="py-24 md:py-40 bg-[#F9F8F6] relative"
      data-testid="services-section"
    >
      <div className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20 md:mb-28">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4"
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px bg-[#C85A43] w-12" />
              <p className="text-sm md:text-base uppercase tracking-[0.3em] font-bold text-[#C85A43]">
                What We Offer
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-8"
          >
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.05] text-[#1C1917]">
              Tax and accounting services{" "}
              <span className="italic font-light text-[#C85A43]">
                tailored to
              </span>{" "}
              the modern professional.
            </h2>
          </motion.div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
